import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRect } from "@reach/rect";
import { Fragment, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { HTML_ATTRIBUTE_HERO_DIALOG_DEFAULT_OPEN } from "@devowl-wp/headless-content-unblocker";
import { extendBlockerHeroStylesheet, extendCommonButtonsStylesheet } from "@devowl-wp/web-cookie-banner";
import { useBlocker } from "../../../contexts/blocker.js";
import { useBlockerStylesheet } from "../../../hooks/blocker/useBlockerStylesheet.js";
import { CloseIcon } from "../../closeIcon.js";
import { Portal } from "../../portal.js";
import { BlockerTextContainer } from "../textContainer.js";
const BlockerHeroDialog = ({ blockerClassName, blockerOverlayId, heroContainerRef: ref, fixed })=>{
    const stylesheet = useBlockerStylesheet();
    const { HeroOverlay, HeroOverlayBlocker, A11ySkipToLink, screenReaderOnlyClass, fixedDialog } = stylesheet.extend(...extendBlockerHeroStylesheet).extend(...extendCommonButtonsStylesheet);
    const blocker = useBlocker();
    const { headerDesign: { fontSize, fontColor }, i18n: { close }, blockedNode } = blocker;
    const refLastActiveElement = useRef();
    const [isDefaultBlockerVisible, setIsDefaultBlockerVisible] = useState(!!blockedNode?.hasAttribute(HTML_ATTRIBUTE_HERO_DIALOG_DEFAULT_OPEN));
    const rect = useRect(ref, {
        observe: isDefaultBlockerVisible
    });
    const overlayRef = useRef();
    // Automatically close on outside click
    const handleOutsideClick = useCallback((e)=>{
        if (isDefaultBlockerVisible && [
            overlayRef.current,
            overlayRef.current?.nextElementSibling
        ].indexOf(e.target) > -1) {
            setIsDefaultBlockerVisible(false);
        }
    }, [
        isDefaultBlockerVisible,
        overlayRef.current
    ]);
    const handleAcessibilityClose = (e)=>{
        // Accessibility, hide hero container and focus previously selected element
        e.preventDefault();
        setIsDefaultBlockerVisible(false);
        refLastActiveElement.current?.focus();
    };
    useEffect(()=>{
        if (ref.current) {
            const listener = (e)=>{
                refLastActiveElement.current = document.activeElement;
                const focusSkipToConsentChoices = e.target.classList.contains(screenReaderOnlyClass);
                // Save reference in data of hero container ref, so we can delegate the click for `confirm()`, see also `delegateClick()`
                if (ref.current) {
                    ref.current.consentDelegateClick = {
                        element: refLastActiveElement.current
                    };
                }
                setIsDefaultBlockerVisible(true);
                setTimeout(()=>{
                    if (focusSkipToConsentChoices) {
                        (overlayRef.current?.nextElementSibling.querySelector(`a[href^="#a"]`)).focus();
                    }
                }, 50);
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            };
            ref.current.addEventListener("click", listener, true);
            return ()=>ref.current?.removeEventListener("click", listener, true);
        }
        return ()=>{
        // Silence is golden.
        };
    }, [
        ref.current
    ]);
    return isDefaultBlockerVisible && rect ? /*#__PURE__*/ _jsx(Portal, {
        renderInContainer: document.body,
        children: /*#__PURE__*/ _jsxs(Fragment, {
            children: [
                /*#__PURE__*/ _jsx(HeroOverlay, {
                    ref: overlayRef,
                    onClick: handleOutsideClick
                }),
                /*#__PURE__*/ _jsxs(HeroOverlayBlocker, {
                    id: blockerOverlayId,
                    rectLeft: rect.left + window.scrollX,
                    rectTop: rect.top + window.scrollY,
                    rectWidth: rect.width,
                    className: `${blockerClassName} ${fixed ? fixedDialog : ""}`,
                    onClick: handleOutsideClick,
                    "interaction-player-uqid": isDefaultBlockerVisible ? "blocker" : undefined,
                    children: [
                        /*#__PURE__*/ _jsx(A11ySkipToLink, {
                            href: "#",
                            onFocus: handleAcessibilityClose,
                            children: "\xa0"
                        }),
                        /*#__PURE__*/ _jsx(Suspense, {
                            fallback: null,
                            children: /*#__PURE__*/ _jsx(BlockerTextContainer, {
                                closeIcon: /*#__PURE__*/ _jsx(CloseIcon, {
                                    tooltipText: close,
                                    width: fontSize,
                                    color: fontColor,
                                    thickness: 2,
                                    onClick: ()=>setIsDefaultBlockerVisible(false)
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(A11ySkipToLink, {
                            href: "#",
                            onFocus: handleAcessibilityClose,
                            children: "\xa0"
                        })
                    ]
                })
            ]
        })
    }) : null;
};
export { BlockerHeroDialog };
